import { IconButton, Tooltip } from '@mui/material';
import { type ReactElement, type ReactNode, useRef } from 'react';
import { useRecoilState } from 'recoil';
import {
    type FloatingPanelId,
    openFloatingPanelState,
} from '~/state/atoms/toolsAtoms';

interface ToolbarButtonProps {
    icon: ReactElement;
    tooltip: ReactNode;
    tooltipPlacement?: 'right' | 'left' | 'bottom';
    selected?: boolean;
    disabled?: boolean;
    panelId?: FloatingPanelId;
    onClick?: () => void;
    anchorRef?: React.MutableRefObject<HTMLElement | null>;
    ariaPressed?: boolean;
    'data-testid'?: string;
}

export default function ToolbarButton({
    icon,
    tooltip,
    tooltipPlacement = 'right',
    selected = false,
    disabled = false,
    panelId,
    onClick,
    anchorRef,
    ariaPressed,
    'data-testid': testId,
}: ToolbarButtonProps) {
    const internalRef = useRef<HTMLButtonElement | null>(null);
    const [openPanel, setOpenPanel] = useRecoilState(openFloatingPanelState);

    const handleClick = () => {
        if (panelId) {
            setOpenPanel((prev) => (prev === panelId ? null : panelId));
        }
        onClick?.();
    };

    const setRef = (el: HTMLButtonElement | null) => {
        internalRef.current = el;
        if (anchorRef) {
            anchorRef.current = el;
        }
    };

    const isPanelOpen = panelId !== undefined && openPanel === panelId;

    return (
        <Tooltip title={tooltip} placement={tooltipPlacement}>
            <span>
                <IconButton
                    ref={setRef}
                    size="small"
                    disabled={disabled}
                    onClick={handleClick}
                    data-testid={testId}
                    aria-label={
                        typeof tooltip === 'string' ? tooltip : undefined
                    }
                    aria-pressed={ariaPressed}
                    sx={(theme) => ({
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        color:
                            selected || isPanelOpen
                                ? theme.palette.primary.main
                                : theme.palette.text.primary,
                        backgroundColor:
                            selected || isPanelOpen
                                ? 'rgba(25, 118, 210, 0.12)'
                                : 'transparent',
                        border:
                            selected || isPanelOpen
                                ? `1px solid ${theme.palette.primary.main}`
                                : '1px solid transparent',
                        padding: '4px',
                        '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                            borderColor: theme.palette.primary.main,
                        },
                        '& svg': {
                            width: '100%',
                            height: '100%',
                            fontSize: 'inherit',
                        },
                    })}
                >
                    {icon}
                </IconButton>
            </span>
        </Tooltip>
    );
}
